///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                                  RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import { createSlice } from "@reduxjs/toolkit";
import { max, parseISO } from "date-fns";
import { orderBy } from "lodash";

import {
  createSystemNotification,
  getNotifications,
  postMarkAsRead
} from "../api/notifications";
import createBrowserNotification from "../helpers/createBrowserNotification";
import { order } from "../helpers/types";
import {
  getAwardManagementExportDocument,
  getLaneErrorDocument,
  getLaneExportDocument
} from "../api/lanes";
import saveFile from "../helpers/saveFile";
import {
  getUserNotificationSettings,
  putNotificationSettings
} from "../api/user";

export const initialState = {
  isFailure: false,
  isFetching: false,
  notifications: [],
  markAsRead: {
    isSaving: false,
    isFailure: false
  },
  systemNotification: {
    isSaving: false,
    isFailure: false,
    isSuccessfullySent: false
  },
  settings: {
    isFetching: false,
    settings: [],
    isFailure: false,
    saveSettingError: false
  }
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    requestNotifications: state => {
      state.isFetching = true;
      state.isFailure = false;
    },
    receiveNotifications: (state, { payload }) => {
      state.notifications = orderBy(payload, "createDateUTC", order.descending);
      state.isFetching = false;
      state.isFailure = false;
    },
    receiveNotificationsFailure: state => {
      state.isFetching = false;
      state.isFailure = true;
    },
    receiveNewNotifications: (state, { payload }) => {
      let newNotifications = payload.data.filter(
        x => x.isPreviouslySeenByUser === false
      );
      state.notifications.push(...payload.data);
      state.notifications = orderBy(
        state.notifications,
        "createDateUTC",
        order.descending
      );

      newNotifications.forEach(n => {
        const isBrowserNotificationSettingEnabled =
          payload.settings.settings.some(
            s =>
              s.notificationSubTypes.includes(n.notificationSubType) &&
              s.browser
          );

        if (isBrowserNotificationSettingEnabled) {
          if (Notification.permission === "granted") {
            createBrowserNotification(n);
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then(permission => {
              if (permission === "granted") {
                createBrowserNotification(n);
              }
            });
          }
        }

        return false;
      });
    },
    requestMarkAllAsRead: state => {
      state.markAsRead = { isSaving: true, isFailure: false };
    },
    markAllAsReadFailure: state => {
      state.markAsRead = { isSaving: false, isFailure: true };
    },
    markAllAsReadReset: state => {
      state.markAsRead = { isSaving: false, isFailure: false };
    },
    requestMarkSingleAsRead: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.markingAsRead = true;
      }
    },
    markSingleAsReadSuccess: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.isPreviouslySeenByUser = true;
        notification.markingAsRead = false;
      }
    },
    markSingleAsReadFailure: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.markingAsRead = false;
      }
    },
    requestDownloadErrorDocument: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingErrorDocument = true;
      }
    },
    resetDownloadErrorDocument: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingErrorDocument = false;
      }
    },
    requestSendSystemNotification: state => {
      state.systemNotification.isSaving = true;
      state.systemNotification.isFailure = false;
      state.systemNotification.isSuccessfullySent = false;
    },
    receiveSendSystemNotificationFailure: state => {
      state.systemNotification.isSaving = false;
      state.systemNotification.isFailure = true;
      state.systemNotification.isSuccessfullySent = false;
    },
    receiveSendSystemNotificationSuccess: state => {
      state.systemNotification.isSaving = false;
      state.systemNotification.isFailure = false;
      state.systemNotification.isSuccessfullySent = true;
    },
    resetSystemNotificationState: state => {
      state.systemNotification = initialState.systemNotification;
    },
    requestNotificationSettings: state => {
      state.settings = {
        ...initialState.settings,
        isFetching: true
      };
    },
    receiveNotificationSettingsSuccess: (state, { payload: settings }) => {
      state.settings = {
        ...initialState.settings,
        settings: settings.map(setting => ({
          browser: setting.browser,
          email: setting.email,
          notificationSubTypes: setting.notificationSubTypes,
          description: setting.description,
          notificationSettingId: setting.defaultNotificationSettingsId
        }))
      };
    },
    receiveNotificationSettingsFailure: state => {
      state.settings = {
        ...initialState.settings,
        isFailure: true,
        isFetching: false
      };
    },
    requestSaveNotificationSetting: (state, { payload }) => {
      payload.forEach(newSettings => {
        const setting = state.settings.settings.find(
          ({ notificationSettingId }) =>
            notificationSettingId === newSettings.notificationSettingId
        );
        setting.unSavedEmail = newSettings.email;
        setting.unSavedBrowser = newSettings.browser;
      });
      state.settings.saveSettingError = false;
    },
    receiveSaveNotificationSettingSuccess: ({ settings }, { payload }) => {
      payload.forEach(s => {
        const setting = settings.settings.find(
          ({ notificationSettingId }) =>
            notificationSettingId === s.notificationSettingId
        );

        setting.email = setting.unSavedEmail;
        setting.browser = setting.unSavedBrowser;
        setting.unSavedEmail = undefined;
        setting.unSavedBrowser = undefined;
      });
      settings.saveSettingError = false;
    },
    toggleSaveSettingErrorModal: (state, { payload: flag }) => {
      state.settings.saveSettingError = flag;
      if (flag === false) {
        state.settings.settings.forEach(s => {
          s.unSavedEmail = undefined;
          s.unSavedBrowser = undefined;
        });
      }
    },
    requestDownloadLaneExportDocument: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingLaneExportDocument = true;
      }
    },
    resetDownloadLaneExportDocument: (state, { payload: notificationId }) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingLaneExportDocument = false;
      }
    },
    requestDownloadAwardManagementExportDocument: (
      state,
      { payload: notificationId }
    ) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingAwardManagementExportDocument = true;
      }
    },
    resetDownloadAwardManagementExportDocument: (
      state,
      { payload: notificationId }
    ) => {
      let notification = state.notifications.find(
        n => n.notificationId === notificationId
      );
      if (notification) {
        notification.downloadingAwardManagementExportDocument = false;
      }
    }
  }
});

export const {
  requestNotifications,
  receiveNotifications,
  receiveNotificationsFailure,
  receiveNewNotifications,
  requestMarkAllAsRead,
  markAllAsReadFailure,
  markAllAsReadReset,
  requestMarkSingleAsRead,
  markSingleAsReadSuccess,
  markSingleAsReadFailure,
  requestDownloadErrorDocument,
  resetDownloadErrorDocument,
  requestSendSystemNotification,
  receiveSendSystemNotificationFailure,
  receiveSendSystemNotificationSuccess,
  resetSystemNotificationState,
  requestNotificationSettings,
  receiveNotificationSettingsSuccess,
  receiveNotificationSettingsFailure,
  requestSaveNotificationSetting,
  receiveSaveNotificationSettingSuccess,
  toggleSaveSettingErrorModal,
  requestDownloadLaneExportDocument,
  resetDownloadLaneExportDocument,
  requestDownloadAwardManagementExportDocument,
  resetDownloadAwardManagementExportDocument
} = notificationsSlice.actions;

export const notificationsReducer = notificationsSlice.reducer;

export const fetchNotifications = callback => async dispatch => {
  dispatch(requestNotifications());
  getNotifications()
    .then(({ data }) => {
      dispatch(receiveNotifications(data));
      callback && callback();
    })
    .catch(() => {
      dispatch(receiveNotificationsFailure());
      callback && callback();
    });
};

export const fetchNewNotfications = () => async (dispatch, getState) => {
  const { notifications, user } = getState();
  if (user?.user?.role !== 0) {
    const maxDate = notifications?.notifications?.length
      ? max(notifications.notifications.map(x => parseISO(x.createDateUTC)))
      : null;
    getNotifications(
      maxDate &&
        new Date(maxDate.getTime() - maxDate.getTimezoneOffset() * 60000)
    ).then(({ data }) => {
      dispatch(
        receiveNewNotifications({ data, settings: notifications.settings })
      );
    });
  }
};

export const markAllAsRead = () => async (dispatch, getState) => {
  const { notifications } = getState().notifications;
  const unreadNotificationIds =
    notifications &&
    notifications
      .filter(n => !n.isPreviouslySeenByUser)
      .map(n => n.notificationId);

  if (unreadNotificationIds && unreadNotificationIds.length) {
    dispatch(requestMarkAllAsRead());
    try {
      await postMarkAsRead(unreadNotificationIds);
      dispatch(markAllAsReadReset());
      dispatch(fetchNotifications());
    } catch {
      dispatch(markAllAsReadFailure());
    }
  }
};

export const markSingleAsRead =
  ({ notificationId, redirect }) =>
  async dispatch => {
    dispatch(requestMarkSingleAsRead(notificationId));
    try {
      await postMarkAsRead([notificationId]);
      dispatch(markSingleAsReadSuccess(notificationId));
      redirect && redirect();
    } catch {
      dispatch(markSingleAsReadFailure(notificationId));
    }
  };

export const downloadLaneErrorDocument =
  ({ bidId, notificationId, uploadId, fileName }) =>
  async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(requestDownloadErrorDocument(notificationId));
    try {
      const response = await getLaneErrorDocument(
        bidId,
        uploadId,
        user.userRoleForManagementUser
      );
      saveFile(response.data, fileName);
      dispatch(resetDownloadErrorDocument(notificationId));
    } catch {
      dispatch(resetDownloadErrorDocument(notificationId));
    }
  };

export const sendSystemNotification = content => async dispatch => {
  dispatch(requestSendSystemNotification());

  try {
    const systemNotificationRequest = { content };
    await createSystemNotification(systemNotificationRequest);
    dispatch(receiveSendSystemNotificationSuccess());
  } catch {
    dispatch(receiveSendSystemNotificationFailure());
  }
};

export const fetchNotificationSettings = () => async dispatch => {
  dispatch(requestNotificationSettings());
  try {
    const response = await getUserNotificationSettings();
    dispatch(receiveNotificationSettingsSuccess(response.data));
  } catch {
    dispatch(receiveNotificationSettingsFailure());
  }
};

export const saveNotificationSettings = settings => async dispatch => {
  dispatch(requestSaveNotificationSetting(settings));
  try {
    await putNotificationSettings(
      settings.map(
        ({ notificationSettingId, notificationSubTypes, email, browser }) => ({
          defaultNotificationSettingsId: notificationSettingId,
          notificationSubTypes,
          email,
          browser
        })
      )
    );
    dispatch(receiveSaveNotificationSettingSuccess(settings));
  } catch {
    dispatch(toggleSaveSettingErrorModal(true));
  }
};

export const downloadLaneExportDocument =
  ({ bidId, notificationId, uploadId, fileName }) =>
  async (dispatch, getState) => {
    const { user } = getState().user;
    dispatch(requestDownloadLaneExportDocument(notificationId));
    try {
      const response = await getLaneExportDocument(
        bidId,
        user.userRoleForManagementUser,
        uploadId
      );
      saveFile(response.data, fileName);
      dispatch(resetDownloadLaneExportDocument(notificationId));
    } catch {
      dispatch(resetDownloadLaneExportDocument(notificationId));
    }
  };

export const downloadAwardManagementExportDocument =
  ({ bidId, notificationId, uploadId, fileName }) =>
  async dispatch => {
    dispatch(requestDownloadAwardManagementExportDocument(notificationId));
    try {
      const response = await getAwardManagementExportDocument(bidId, uploadId);
      saveFile(response.data, fileName);
      dispatch(resetDownloadAwardManagementExportDocument(notificationId));
    } catch {
      dispatch(resetDownloadAwardManagementExportDocument(notificationId));
    }
  };
